/** 출제 응답에서 현재 학생의 배정 id를 고른다. */
export function assignmentIdForStudent(data, studentId) {
  const assigned = Array.isArray(data?.assigned) ? data.assigned : [];
  const sid = studentId == null ? null : String(studentId);
  const match = sid == null
    ? assigned[0]
    : assigned.find((entry) => String(entry?.student_id) === sid) || assigned[0];
  return match?.assignment_id ?? match?.id ?? null;
}

/**
 * 선택 판정 키 — v1 테스트와 v2 배정이 **한 자리**를 나눠 쓴다.
 *
 * 문제 미리보기(ProblemPreviewPanel)의 `selectedTest` 는 원래 v1 테스트만 앉던 자리인데,
 * 2026-09-07 부터 v2 시험지 배정도 같은 자리에 앉는다(오너 지시 — 배정 카드를 누르면 구 테스트
 * 카드처럼 골라지고 미리보기가 그 시험지로 좁혀진다). 하나만 골라지는 자리라 상태를 둘로
 * 늘리지 않았다.
 *
 * ⚠ `exam_id` 와 `assignment_id` 는 **다른 번호 공간**이다 — 시험 23 과 배정 23 이 함께 있을 수
 * 있어, 값만 견주면 남의 카드가 골라진 것으로 읽힌다. 앞글자로 갈라 둔다.
 */
export function testKey(t) {
  if (!t) return null;
  if (t.kind === 'assignment') return t.assignment_id == null ? null : `a-${t.assignment_id}`;
  return t.exam_id == null ? null : `t-${t.exam_id}`;
}

/** 카드가 말하는 시험지 이름 — 제목이 없으면 번호로 부른다(HubAssignmentCard 와 같은 자) */
const titleOf = (row, detail) => detail?.paper_title
  || row?.paper_title
  || (row?.paper_id != null ? `시험지 #${row.paper_id}` : (detail?.paper_id != null ? `시험지 #${detail.paper_id}` : '시험지'));

/**
 * 아직 문항이 오지 않은 배정.
 *
 * 배정 **목록** 행에는 문항이 없다(계약 §1-1 — 수만 온다). 문항은 상세를 한 번 더 물어야
 * 오는데, 그 왕복을 기다렸다 카드를 칠하면 누른 뒤 한동안 아무 일도 일어나지 않는다. 그래서
 * 고르는 즉시 이것을 앉히고 — 카드는 곧바로 선택되고 미리보기는 「불러오는 중」이 된다 —
 * 문항은 도착하면 assignmentToTest 로 덮는다.
 */
export function assignmentPending(row) {
  const aid = row?.assignment_id ?? null;
  return {
    kind: 'assignment',
    pending: true,
    assignment_id: aid,
    exam_id: null,
    tree_name: titleOf(row, null),
    problems_list: [],
    result: {},
  };
}

/**
 * 배정 상세(계약 §2-9)를 **문제 미리보기가 아는 「테스트」 모양**으로 옮긴다.
 *
 * 미리보기가 읽는 것은 딱 둘이다 — 문항 목록 `problems_list`(`{problem_id, step, address}`)와
 * 정오답 맵 `result[problem_id]`. 배정 상세의 `items[]` 가 그 둘을 다 갖고 있으므로 옮기기만
 * 하면 되고, 미리보기 쪽 코드는 v1 인지 v2 인지 알 필요가 없다.
 *
 * `step`·`address` 는 **비워 둔다.** 배정 상세에 없는 값이라 지어낼 수 없다 — 카드에서 난이도와
 * 주소 자리가 빌 뿐, 없는 것을 있는 것처럼 그리지 않는다.
 *
 * 채점값은 3-state 다(true/false/null · §2-9). null 을 그대로 실어야 「틀림」과 「아직 안 매김」이
 * 갈린다 — false 로 눌러 두면 미채점 문항이 전부 오답으로 물든다.
 */
export function assignmentToTest(detail, row = null) {
  const aid = detail?.assignment_id ?? row?.assignment_id ?? null;
  const items = Array.isArray(detail?.items) ? detail.items : [];
  const problems_list = [];
  const result = {};
  items.forEach((it) => {
    const pid = it?.problem_id;
    if (pid == null) return;
    problems_list.push({ problem_id: pid, step: null, address: null });
    result[pid] = it?.result === true ? true : (it?.result === false ? false : null);
  });
  return {
    kind: 'assignment',
    assignment_id: aid,
    exam_id: null,
    tree_name: titleOf(row, detail),
    problems_list,
    result,
  };
}

export default assignmentIdForStudent;
